import apiFetch from '@wordpress/api-fetch'
import { addQueryArgs } from '@wordpress/url'
import { create } from 'zustand'

type WpTitle = { rendered?: string }
type WpExcerpt = { rendered?: string; protected?: boolean }

export interface I_WpItem {
  id: number
  link?: string
  title?: WpTitle
  excerpt?: WpExcerpt
  acf?: Record<string, string | boolean | { value_new?: string | null }>
}

// SystemPost inherits all WpItem fields, and can add its own:
export interface I_SystemPost extends I_WpItem {
  // // optional: override or make stricter
  // title: WpTitle;
  // excerpt: WpExcerpt;
  // // custom SystemPost-specific props
  // systemCategory?: string;
  acf?: I_WpItem['acf'] & {
    icon?: I_ACFImage
    introduction?: string
    accordion?: I_AccordionSection[]
    related_case_studies?: I_CasePostObject[]
  }
}

interface I_CasePostObject {
  ID: number
  post_title: string
}

interface I_SystemPostObject {
  ID: number
  post_title: string
}

// CasePost also extends WpItem independently
export interface I_CasePost extends I_WpItem {
  // // custom CasePost-specific props
  // related_systems?: number[];
  // location?: string;
  acf?: I_WpItem['acf'] & {
    introduction?: string
    accordion?: I_AccordionSection[]
    featured_images?: {
      image?: I_ACFImage
    }[]
    attachment?: I_ACFFile
    hvac_system_to?: I_SystemPostObject
  }
}

export interface I_AccordionSection {
  acf_fc_layout: 'accordion_section'
  title: string
  content: string
}

export interface I_ACFImage {
  ID?: number
  id?: number
  url?: string
  alt?: string
  caption?: string
  width?: number
  height?: number
  sizes?: Record<string, string | number> // ACF's mixed map of size URLs + width/height
  [key: string]: unknown // keep everything else without typing it
}

export interface I_ACFFile {
  ID?: number
  id?: number
  title?: string
  filename?: string
  filesize?: number // bytes
  url?: string
  link?: string
  alt?: string
  author?: string
  description?: string
  caption?: string
  name?: string
  status?: string
  uploaded_to?: number
  date?: string
  modified?: string
  menu_order?: number
  mime_type?: string // e.g. "application/pdf"
  type?: string // e.g. "application"
  subtype?: string // e.g. "pdf"
  icon?: string
  [key: string]: unknown
}

type WpDataState = {
  systems: I_SystemPost[] | null
  cases: I_CasePost[] | null
  loading: boolean
  error: string | null
  lastLoadedAt: number | null

  // actions
  loadAll: (opts?: { perPage?: number; fetchAll?: boolean }) => Promise<void>
  clear: () => void

  // getters
  getSystemById: (id: number | undefined) => I_SystemPost | undefined
  getCaseById: (id: number | undefined) => I_CasePost | undefined
}

const DEFAULT_FIELDS = ['id', 'title', 'link', 'excerpt', 'acf'] as const

type Envelope<T> = {
  body: T
  headers: Record<string, string>
  status: number
}

async function fetchCollection(
  postType: 'smartguide_systems' | 'smartguide_cases',
  perPage = 20,
  fields: readonly string[] = DEFAULT_FIELDS,
  fetchAllPages = true
): Promise<I_WpItem[]> {
  // NOTE (Option B): apiFetch root is '.../wp-json/wp/v2/', so paths are relative to that root.
  const common = {
    per_page: Math.min(perPage, 100),
    _fields: fields.join(','),
    acf_format: 'standard' as const,
  }

  // First page via _envelope=1 to read headers in the JSON (typed, no parse:false)
  const firstPath = addQueryArgs(`/${postType}`, {
    ...common,
    page: 1,
    _envelope: 1,
  }) as string
  const firstEnv = await apiFetch<Envelope<I_WpItem[]>>({ path: firstPath })

  const firstItems = firstEnv.body
  // const totalPages = parseInt(firstEnv.headers['x-wp-totalpages'] || '1', 10)

  const totalPagesHeader =
    firstEnv.headers['x-wp-totalpages'] ??
    firstEnv.headers['X-WP-TotalPages'] ??
    Object.entries(firstEnv.headers).find(
      ([k]) => k.toLowerCase() === 'x-wp-totalpages'
    )?.[1]

  const totalPages = parseInt(totalPagesHeader || '1', 10)

  if (!fetchAllPages || totalPages <= 1) return firstItems

  // Fetch remaining pages 2..N (no envelope needed for these)
  const rest = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, i) => i + 2).map((page) =>
      apiFetch<I_WpItem[]>({
        path: addQueryArgs(`/${postType}`, { ...common, page }) as string,
      })
    )
  )

  return rest.reduce<I_WpItem[]>((acc, part) => acc.concat(part), firstItems)
}

export const useWpDataStore = create<WpDataState>((set, get) => ({
  systems: null,
  cases: null,
  loading: false,
  error: null,
  lastLoadedAt: null,

  clear: () =>
    set({ systems: null, cases: null, error: null, lastLoadedAt: null }),

  loadAll: async ({ perPage = 20, fetchAll = true } = {}) => {
    // avoid duplicate loads if already loading
    if (get().loading) return
    set({ loading: true, error: null })

    try {
      const [systems, cases] = await Promise.all([
        fetchCollection(
          'smartguide_systems',
          perPage,
          DEFAULT_FIELDS,
          fetchAll
        ),
        fetchCollection('smartguide_cases', perPage, DEFAULT_FIELDS, fetchAll),
      ])

      set({
        systems,
        cases,
        loading: false,
        error: null,
        lastLoadedAt: Date.now(),
      })
    } catch (e: any) {
      set({
        loading: false,
        error: e?.message
          ? String(e.message)
          : 'Failed to load Smart Guide data',
      })
    }
  },

  getSystemById: (id) =>
    (get().systems ?? undefined)?.find((p) => p.id === id) as
      | I_SystemPost
      | undefined,

  getCaseById: (id) =>
    (get().cases ?? undefined)?.find((p) => p.id === id) as
      | I_CasePost
      | undefined,
}))
